import React, { useRef, useMemo } from "react";
import { useFrame } from "@react-three/fiber";
import { Group, Texture } from "three";
import { WindowSize, GridSize, GlasspanelPosition } from "../../../../../../models/door_configuration";
import {
  MM,
  ALTEZZA_PANNELLO_M,
  OverbasStep,
  IntegratedDoorPosition,
} from "./constants";
import { getPortinaX, Portina } from "./portina_integrata";
import { PannelloSingolo } from "./pannello_singolo";
import { ManigliaLock, ManigliaSquare } from "./maniglia_basculante";

function smoothstep(t: number) { return t * t * (3 - 2 * t); }

// Posizione e rotazione di una sezione rigida (metà porta).
// Le due sezioni finiscono a uguale Y, sfalsate di uno spessore in Z — come un panino.
function sectionTransform(
  progress: number,
  startAt: number,      // progress da cui inizia questa sezione
  duration: number,     // quanto progress ci vuole per completare (top più corta = più veloce)
  closedY: number,
  sectionHalfH: number, // altezza/4
  doorHeight: number,
  targetY: number,
  targetZ: number,
): { panelY: number; panelZ: number; rotX: number } {
  const localP = Math.max(0, Math.min(1, (progress - startAt) / duration));
  if (localP <= 0) return { panelY: closedY, panelZ: 0, rotX: 0 };
  if (localP >= 1) return { panelY: targetY, panelZ: targetZ, rotX: -Math.PI / 2 };

  if (localP < 0.5) {
    const t = smoothstep(localP * 2);
    return {
      panelY: closedY + (doorHeight - sectionHalfH - closedY) * t,
      panelZ: 0,
      rotX: 0,
    };
  } else {
    const t     = smoothstep((localP - 0.5) * 2);
    const theta = t * Math.PI / 2;
    return {
      panelY: doorHeight - sectionHalfH * Math.cos(theta) + (targetY - doorHeight) * t,
      panelZ: -sectionHalfH * Math.sin(theta)             + (targetZ - (-sectionHalfH)) * t,
      rotX: -theta,
    };
  }
}

export type PannelliAnimatiProps = {
  larghezza: number;
  altezza: number;
  spessore: number;
  isOpen: boolean;
  showPortina: boolean;
  portinaPosizione: IntegratedDoorPosition;
  portinaLarghezza: number;
  portinaColore: Texture | undefined;
  portinaColoreInterno: Texture | undefined;
  portinaManigliaCOlore: string;
  portinaManigliaCOloreInterno: string;
  portinaSogliaAltezza: number;
  portinaApertura: "sx" | "dx";
  step: OverbasStep;
  finestreAttive: Set<string>;
  griglieAttive: Set<string>;
  onFinestraClick: (panelIndex: number, finestraIndex: number) => void;
  colorePannello: Texture | undefined;
  coloreInternoPannello: Texture | undefined;
  corniceFinestratura: Texture | undefined;
  isWide: boolean;
  isFullVision: boolean;
  panelRows: number;
  campate: number[];
  vetroSatinato: boolean;
  windowSize: WindowSize;
  glasspanelAltezza?: number;
  glasspanelPosizione?: GlasspanelPosition;
  hoverColor: string;
  gridSize: GridSize;
  grigliaColor: string | undefined;
  tipoManiglia?: string | null;
  rawAltezza?: number;
  coloreManiglia?: string;
};

export const PannelliAnimati: React.FC<PannelliAnimatiProps> = ({
  larghezza,
  altezza,
  spessore,
  isOpen,
  showPortina,
  portinaPosizione,
  portinaLarghezza,
  portinaColore,
  portinaColoreInterno,
  portinaManigliaCOlore,
  portinaManigliaCOloreInterno,
  portinaSogliaAltezza,
  portinaApertura,
  step,
  finestreAttive,
  griglieAttive,
  onFinestraClick,
  colorePannello,
  coloreInternoPannello,
  corniceFinestratura,
  isWide,
  isFullVision,
  panelRows,
  campate,
  vetroSatinato,
  windowSize,
  glasspanelAltezza,
  glasspanelPosizione,
  hoverColor,
  gridSize,
  grigliaColor,
  tipoManiglia,
  rawAltezza = 0,
  coloreManiglia,
}) => {
  const progressRef    = useRef(0);
  const topGroupRef    = useRef<Group>(null);
  const botGroupRef    = useRef<Group>(null);
  const accessoriesRef = useRef<Group>(null);

  const panels = useMemo(() => {
    const list: { y: number; h: number; index: number }[] = [];
    if (panelRows > 0) {
      const h = altezza / panelRows;
      for (let i = 0; i < panelRows; i++) list.push({ y: h * i + h / 2, h, index: i });
    } else {
      let y = 0;
      let index = 0;
      while (y < altezza) {
        const h = Math.min(ALTEZZA_PANNELLO_M, altezza - y);
        list.push({ y: y + h / 2, h, index });
        y += h;
        index++;
      }
    }
    return list;
  }, [altezza, panelRows]);

  // Indipendentemente dal numero di pannelli reali, l'animazione usa sempre
  // due sezioni rigide: metà superiore (groupIndex=1) e metà inferiore (groupIndex=0).
  const halfH    = altezza / 2;
  const botCtrY  = altezza / 4;       // centro sezione inferiore
  const topCtrY  = altezza * 3 / 4;   // centro sezione superiore

  const portinaX    = getPortinaX(portinaPosizione, larghezza, portinaLarghezza);
  const isDoorClosed = progressRef.current < 0.1;

  const isGlasspanel = windowSize === WindowSize.glasspanel ||
    windowSize === WindowSize.glasspanel_vetro_6_7 ||
    windowSize === WindowSize.glasspanel_vetro_5 ||
    windowSize === WindowSize.glasspanel_vetro_4_12_4;

  const glasspanelLimitReached = useMemo(() => {
    if (!isGlasspanel) return false;
    const eligibili = panels.filter(p => p.h > MM(200));
    if (eligibili.length !== 5) return false;
    const attivi = eligibili.filter(p => finestreAttive.has(`${p.index}-0`)).length;
    return attivi >= 4;
  }, [isGlasspanel, panels, finestreAttive]);

  useFrame(() => {
    const target = isOpen ? 1 : 0;
    progressRef.current = Math.max(
      0,
      Math.min(1, progressRef.current + (target - progressRef.current) * (target > progressRef.current ? 0.003 : 0.003)),
    );

    const p          = progressRef.current;
    const sectionH   = altezza / 2;
    const halfSection = sectionH / 2; // = altezza/4

    // Z finale uguale per entrambe le sezioni: stessa profondità nel garage
    const openZ = -halfSection; // = -altezza/4

    // Superiore: parte subito, curva inizia a progress=0.3
    const top = sectionTransform(p, 0,   0.6, topCtrY, halfSection, altezza, altezza,           openZ);
    // Inferiore: inizia a salire proprio quando la superiore comincia a curvare (progress=0.3)
    const bot = sectionTransform(p, 0.3, 0.7, botCtrY, halfSection, altezza, altezza - spessore, openZ);

    if (topGroupRef.current) {
      topGroupRef.current.position.set(0, top.panelY, top.panelZ);
      topGroupRef.current.rotation.x = top.rotX;
    }
    if (botGroupRef.current) {
      botGroupRef.current.position.set(0, bot.panelY, bot.panelZ);
      botGroupRef.current.rotation.x = bot.rotX;
    }
    if (accessoriesRef.current) {
      accessoriesRef.current.position.set(0, bot.panelY, bot.panelZ);
      accessoriesRef.current.rotation.x = bot.rotX;
    }
  });

  const botPanels = panels.filter(p => p.y <= altezza / 2);
  const topPanels = panels.filter(p => p.y >  altezza / 2);

  // Pannello immediatamente sopra il centro porta (per la serratura) e secondo
  // pannello dal basso (per il quadratino semplice) della doppia maniglia. Con
  // soli 2 pannelli (es. plana a fasce larghe) il "secondo dal basso" coincide
  // col pannello della serratura: in quel caso il quadratino scende sul primo
  // pannello, cosi' resta visibile sotto la serratura invece di sparirci dietro.
  const manigliaLockPanel      = topPanels[0] ?? panels[panels.length - 1] ?? { y: altezza / 2, index: -1 };
  const manigliaSquareCandidate = panels[1] ?? panels[0] ?? { y: altezza / 2, index: -1 };
  const manigliaSquarePanel    = manigliaSquareCandidate.index === manigliaLockPanel.index
    ? (panels[0] ?? manigliaSquareCandidate)
    : manigliaSquareCandidate;
  const manigliaLockY   = manigliaLockPanel.y;
  const manigliaSquareY = manigliaSquarePanel.y;

  // Ogni pezzo della doppia maniglia deve muoversi con la sezione (superiore o
  // inferiore) del pannello su cui e' effettivamente montato: se lo si anima
  // insieme alla sezione sbagliata, durante l'apertura si stacca dal pannello
  // e sembra fluttuare invece di seguirlo.
  const lockInTop   = manigliaLockY   > altezza / 2;
  const squareInTop = manigliaSquareY > altezza / 2;

  return (
    <group position={[larghezza / 2, 0, 0]}>

      {/* ── Sezione inferiore (si muove per seconda) ── */}
      <group ref={botGroupRef} position={[0, botCtrY, 0]}>
        {botPanels.map(({ y, h, index }) => (
          <group key={index} position={[0, y - botCtrY, 0]}>
            <PannelloSingolo
              larghezza={larghezza}
              altezza={h}
              spessore={spessore}
              panelIndex={index}
              panelWorldY={y}
              showPortina={showPortina}
              portinaX={portinaX}
              portinaLarghezza={portinaLarghezza}
              step={step}
              finestreAttive={finestreAttive}
              griglieAttive={griglieAttive}
              onFinestraClick={onFinestraClick}
              isDoorClosed={isDoorClosed}
              colorePannello={colorePannello}
              coloreInternoPannello={coloreInternoPannello}
              corniceFinestratura={corniceFinestratura}
              isWide={isWide}
              isFullVision={isFullVision}
              campate={campate}
              vetroSatinato={vetroSatinato}
              windowSize={windowSize}
              glasspanelLimitReached={glasspanelLimitReached}
              glasspanelAltezza={glasspanelAltezza}
              glasspanelPosizione={glasspanelPosizione}
              hoverColor={hoverColor}
              gridSize={gridSize}
              grigliaColor={grigliaColor}
              tipoManiglia={tipoManiglia}
              manigliaLockY={manigliaLockY}
              manigliaSquareY={manigliaSquareY}
            />
          </group>
        ))}
        {!lockInTop && (
          <ManigliaLock y={manigliaLockY - botCtrY} spessore={spessore} tipoManiglia={tipoManiglia ?? null} coloreManiglia={coloreManiglia} />
        )}
        {!squareInTop && (
          <ManigliaSquare y={manigliaSquareY - botCtrY} spessore={spessore} tipoManiglia={tipoManiglia ?? null} coloreManiglia={coloreManiglia} />
        )}
      </group>

      {/* ── Sezione superiore (si muove per prima) ── */}
      <group ref={topGroupRef} position={[0, topCtrY, 0]}>
        {topPanels.map(({ y, h, index }) => (
          <group key={index} position={[0, y - topCtrY, 0]}>
            <PannelloSingolo
              larghezza={larghezza}
              altezza={h}
              spessore={spessore}
              panelIndex={index}
              panelWorldY={y}
              showPortina={showPortina}
              portinaX={portinaX}
              portinaLarghezza={portinaLarghezza}
              step={step}
              finestreAttive={finestreAttive}
              griglieAttive={griglieAttive}
              onFinestraClick={onFinestraClick}
              isDoorClosed={isDoorClosed}
              colorePannello={colorePannello}
              coloreInternoPannello={coloreInternoPannello}
              corniceFinestratura={corniceFinestratura}
              isWide={isWide}
              isFullVision={isFullVision}
              campate={campate}
              vetroSatinato={vetroSatinato}
              windowSize={windowSize}
              glasspanelLimitReached={glasspanelLimitReached}
              glasspanelAltezza={glasspanelAltezza}
              glasspanelPosizione={glasspanelPosizione}
              hoverColor={hoverColor}
              gridSize={gridSize}
              grigliaColor={grigliaColor}
              tipoManiglia={tipoManiglia}
              manigliaLockY={manigliaLockY}
              manigliaSquareY={manigliaSquareY}
            />
          </group>
        ))}
        {lockInTop && (
          <ManigliaLock y={manigliaLockY - topCtrY} spessore={spessore} tipoManiglia={tipoManiglia ?? null} coloreManiglia={coloreManiglia} />
        )}
        {squareInTop && (
          <ManigliaSquare y={manigliaSquareY - topCtrY} spessore={spessore} tipoManiglia={tipoManiglia ?? null} coloreManiglia={coloreManiglia} />
        )}
      </group>

      {/* ── Portina: segue la sezione inferiore ── */}
      <group ref={accessoriesRef} position={[0, botCtrY, 0]}>
        {/* shift per riportare l'origine a altezza/2, come si aspetta il componente */}
        <group position={[0, altezza / 2 - botCtrY, 0]}>
          {showPortina && (
            <Portina
              spessore={spessore}
              altezza={altezza}
              portinaX={portinaX}
              portinaLarghezza={portinaLarghezza}
              colore={portinaColore}
              coloreInterno={portinaColoreInterno}
              manigliaColore={portinaManigliaCOlore}
              manigliaColoreInterno={portinaManigliaCOloreInterno}
              sogliaAltezza={portinaSogliaAltezza}
              apertura={portinaApertura}
            />
          )}
        </group>
      </group>
    </group>
  );
};
